<script lang="ts">
  import FieldLabel from './FieldLabel.svelte'
  import { utils } from '../../utils'
  import type { HTMLSelectAttributes } from 'svelte/elements'
  import FieldError from './FieldError.svelte'
  import FieldHint from './FieldHint.svelte'
  import Icon from './Icon.svelte'

  type SelectVariant = 'contained' | 'outlined' | 'text'

  interface $$Props extends HTMLSelectAttributes {
    value?: string | number | null
    /** The caption for the associated input. */
    label?: string
    /** Error message for the associated form control. If there are multiple errors, only the first will be shown. */
    error?: string | string[]
    /** Helper text for providing context to the input. The hint will be hidden if there are any errors. */
    hint?: string
    name?: string
    rounded?: boolean
    variant?: SelectVariant
    appendIcon?: string
  }

  export let value: string | number | null = ''
  export let label: string | undefined = undefined
  export let error: string | string[] | undefined = undefined
  export let hint: string | undefined = undefined
  export let name: string | undefined = undefined
  export let rounded = false
  export let variant: SelectVariant = 'contained'
  export let appendIcon: string | undefined = undefined

  let select: HTMLSelectElement
  const id = $$props.id || `select-${utils.getRandomString(8)}`

  export function focus() {
    select.focus()
  }

  export function blur() {
    select.blur()
  }
</script>

<div class="select-field">
  {#if label}
    <FieldLabel invalid={!!error} htmlFor={id}>{label}</FieldLabel>
  {/if}
  <div class="select-container" class:label>
    <select
      bind:value
      {id}
      {name}
      {...$$restProps}
      bind:this={select}
      on:change
      class:rounded
      class={variant}
      class:appendIcon
    >
      <slot />
    </select>
    {#if appendIcon}
      <div class:appendIcon>
        <Icon icon={appendIcon} />
      </div>
    {/if}
  </div>
  {#if error}
    <FieldError {error} />
  {/if}
  {#if hint && !error}
    <FieldHint {hint} />
  {/if}
</div>

<style lang="scss">
  .select-field {
    display: flex;
    flex-direction: column;
  }

  .select-container {
    position: relative;
    display: flex;
    align-items: center;
    margin: var(--input-margin);
    font-size: 16px;

    &:after {
      content: '\e136';
      font-family: 'Phosphor-Fill';
      position: absolute;
      right: 0.75em;
      font-size: 0.85em;
      color: var(--color-on-surface);
      pointer-events: none;
    }

    &:not(.label) {
      margin-top: 0;
    }

    select {
      box-sizing: border-box;
      height: var(--input-height);
      padding: var(--input-padding);
      padding-right: 2em;
      background: var(--input-background);
      border: var(--input-border);
      border-radius: var(--input-border-radius);
      box-shadow: var(--input-box-shadow);
      transition: background-color 0.125s;
      appearance: none;
      font-family: 'Helvetica Neue', sans-serif;
      font-size: 1em;
      flex: 1;
      color: var(--color-on-surface);

      :global(option),
      :global(optgroup) {
        background-color: var(--color-surface);
      }

      &.rounded {
        border-radius: calc(1em * 2);
        padding-left: 0.85em;
      }

      &.outlined {
        background: transparent;
        border: 1px solid rgba(var(--color-on-surface-rgb), 0.38);
        transition: border 250ms cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: unset;

        &:hover {
          border: 1px solid rgba(var(--color-on-surface-rgb), 0.87);
        }

        &:focus {
          border: 1px solid rgba(var(--color-on-surface-rgb), 1);
          outline: none;
        }
      }

      &.text {
        background: transparent;
        border: none;
        box-shadow: unset;
        text-decoration: underline;
        padding-left: 0;

        &:focus {
          outline: none;
        }
      }

      &.appendIcon {
        padding-right: 4em;
      }
    }

    div.appendIcon {
      position: absolute;
      right: 1.5em;
      pointer-events: none;
    }
  }
</style>
